import "core-js/modules/es.array.flat.js";
import "core-js/modules/es.array.unscopables.flat.js";
import { __awaiter, __generator, __read, __spreadArray } from "tslib";
import { PluginType, Status } from '@amplitude/analytics-types';
import { MISSING_API_KEY_MESSAGE, SUCCESS_MESSAGE, UNEXPECTED_ERROR_MESSAGE } from '../messages';
import { STORAGE_PREFIX } from '../constants';
import { chunk } from '../utils/chunk';
import { buildResult } from '../utils/result-builder';
import { createServerConfig } from '../config';

var Destination =
/** @class */
function () {
  function Destination() {
    this.name = 'amplitude';
    this.type = PluginType.DESTINATION;
    this.backoff = 1000;
    this.throttle = 30000;
    this.storageKey = '';
    this.backup = new Set();
    this.scheduled = false;
    this.queue = [];
  }

  Destination.prototype.setup = function (config) {
    var _this = this;

    this.config = config;
    this.storageKey = "".concat(STORAGE_PREFIX, "_").concat(this.config.apiKey.substring(0, 10));
    var unsent = this.config.storageProvider.get(this.storageKey);
    this.snapshot(); // sets storage to '[]'

    if (unsent && unsent.length > 0) {
      void Promise.all(unsent.map(function (event) {
        return _this.execute(event);
      })).catch();
    }

    return Promise.resolve(undefined);
  };

  Destination.prototype.execute = function (event) {
    var _this = this;

    return new Promise(function (resolve) {
      var context = {
        event: event,
        attempts: 0,
        callback: function callback(result) {
          return resolve(result);
        },
        delay: 0
      };

      _this.addToQueue(context);
    });
  };

  Destination.prototype.addToQueue = function () {
    var _this = this;

    var list = [];

    for (var _i = 0; _i < arguments.length; _i++) {
      list[_i] = arguments[_i];
    }

    var tryable = list.filter(function (context) {
      if (context.attempts < _this.config.flushMaxRetries) {
        return true;
      }

      _this.fulfillRequest([context], 500, Status.Unknown);

      return false;
    });
    this.addToBackup.apply(this, __spreadArray([], __read(tryable.map(function (context) {
      return context.event;
    })), false));
    tryable.forEach(function (context) {
      context.attempts += 1;
      var delay = context.delay;
      context.delay = 0;

      if (context.attempts === 1 && !context.delay) {
        _this.queue = _this.queue.concat(context);

        _this.schedule(_this.config.flushIntervalMillis);

        return;
      }

      setTimeout(function () {
        _this.queue = _this.queue.concat(context);

        _this.schedule(_this.config.flushIntervalMillis);
      }, delay || context.attempts * _this.backoff);
    });
  };

  Destination.prototype.schedule = function (timeout) {
    var _this = this;

    if (this.scheduled) return;
    this.scheduled = true;
    setTimeout(function () {
      void _this.flush().then(function () {
        _this.scheduled = false;

        if (_this.queue.length > 0) {
          _this.schedule(timeout);
        }
      });
    }, timeout);
  };

  Destination.prototype.flush = function () {
    return __awaiter(this, void 0, void 0, function () {
      var list, batches;

      var _this = this;

      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            list = this.queue;
            this.queue = [];
            batches = chunk(list, this.config.flushQueueSize);
            return [4
            /*yield*/
            , Promise.all(batches.map(function (batch) {
              return _this.send(batch);
            }))];

          case 1:
            _a.sent();

            return [2
            /*return*/
            ];
        }
      });
    });
  };

  Destination.prototype.send = function (list) {
    return __awaiter(this, void 0, void 0, function () {
      var payload, serverUrl, res, e_1;
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            if (!this.config.apiKey) {
              return [2
              /*return*/
              , this.fulfillRequest(list, 400, MISSING_API_KEY_MESSAGE)];
            }

            payload = {
              api_key: this.config.apiKey,
              events: list.map(function (context) {
                return context.event;
              }),
              options: {
                min_id_length: this.config.minIdLength
              }
            };
            _a.label = 1;

          case 1:
            _a.trys.push([1, 3,, 4]);

            serverUrl = createServerConfig(this.config.serverUrl, this.config.serverZone, this.config.useBatch).serverUrl;
            return [4
            /*yield*/
            , this.config.transportProvider.send(serverUrl, payload)];

          case 2:
            res = _a.sent();

            if (res === null) {
              this.fulfillRequest(list, 0, UNEXPECTED_ERROR_MESSAGE);
              return [2
              /*return*/
              ];
            }

            this.handleReponse(res, list);
            return [3
            /*break*/
            , 4];

          case 3:
            e_1 = _a.sent();
            this.fulfillRequest(list, 0, String(e_1));
            return [3
            /*break*/
            , 4];

          case 4:
            return [2
            /*return*/
            ];
        }
      });
    });
  };

  Destination.prototype.handleReponse = function (res, list) {
    var status = res.status;

    switch (status) {
      case Status.Success:
        this.handleSuccessResponse(res, list);
        break;

      case Status.Invalid:
        this.handleInvalidResponse(res, list);
        break;

      case Status.PayloadTooLarge:
        this.handlePayloadTooLargeResponse(res, list);
        break;

      case Status.RateLimit:
        this.handleRateLimitResponse(res, list);
        break;

      default:
        this.handleOtherReponse(list);
    }
  };

  Destination.prototype.handleSuccessResponse = function (res, list) {
    this.fulfillRequest(list, res.statusCode, SUCCESS_MESSAGE);
  };

  Destination.prototype.handleInvalidResponse = function (res, list) {
    var _this = this;

    if (res.body.missingField) {
      this.fulfillRequest(list, res.statusCode, res.body.error);
      return;
    }

    var dropIndex = __spreadArray(__spreadArray(__spreadArray(__spreadArray([], __read(Object.values(res.body.eventsWithInvalidFields)), false), __read(Object.values(res.body.eventsWithMissingFields)), false), __read(Object.values(res.body.eventsWithInvalidIdLengths)), false), __read(res.body.silencedEvents), false).flat();

    var dropIndexSet = new Set(dropIndex);
    var retry = list.filter(function (context, index) {
      if (dropIndexSet.has(index)) {
        _this.fulfillRequest([context], res.statusCode, res.body.error);

        return;
      }

      return true;
    });
    this.addToQueue.apply(this, __spreadArray([], __read(retry), false));
  };

  Destination.prototype.handlePayloadTooLargeResponse = function (res, list) {
    if (list.length === 1) {
      this.fulfillRequest(list, res.statusCode, res.body.error);
      return;
    }

    this.config.flushQueueSize /= 2;
    this.addToQueue.apply(this, __spreadArray([], __read(list), false));
  };

  Destination.prototype.handleRateLimitResponse = function (res, list) {
    var _this = this;

    var dropUserIds = Object.keys(res.body.exceededDailyQuotaUsers);
    var dropDeviceIds = Object.keys(res.body.exceededDailyQuotaDevices);
    var throttledIndex = res.body.throttledEvents;
    var dropUserIdsSet = new Set(dropUserIds);
    var dropDeviceIdsSet = new Set(dropDeviceIds);
    var throttledIndexSet = new Set(throttledIndex);
    var retry = list.filter(function (context, index) {
      if (context.event.user_id && dropUserIdsSet.has(context.event.user_id) || context.event.device_id && dropDeviceIdsSet.has(context.event.device_id)) {
        _this.fulfillRequest([context], res.statusCode, res.body.error);

        return;
      }

      if (throttledIndexSet.has(index)) {
        context.delay = _this.throttle;
      }

      return true;
    });
    this.addToQueue.apply(this, __spreadArray([], __read(retry), false));
  };

  Destination.prototype.handleOtherReponse = function (list) {
    this.addToQueue.apply(this, __spreadArray([], __read(list), false));
  };

  Destination.prototype.fulfillRequest = function (list, code, message) {
    this.removeFromBackup.apply(this, __spreadArray([], __read(list.map(function (context) {
      return context.event;
    })), false));
    list.forEach(function (context) {
      return context.callback(buildResult(context.event, code, message));
    });
  };

  Destination.prototype.addToBackup = function () {
    var _this = this;

    var events = [];

    for (var _i = 0; _i < arguments.length; _i++) {
      events[_i] = arguments[_i];
    }

    if (!this.config.saveEvents) return;
    events.forEach(function (event) {
      return _this.backup.add(event);
    });
    this.snapshot();
  };

  Destination.prototype.removeFromBackup = function () {
    var _this = this;

    var events = [];

    for (var _i = 0; _i < arguments.length; _i++) {
      events[_i] = arguments[_i];
    }

    if (!this.config.saveEvents) return;
    events.forEach(function (event) {
      return _this.backup.delete(event);
    });
    this.snapshot();
  };

  Destination.prototype.snapshot = function () {
    if (!this.config.saveEvents) return;
    var events = Array.from(this.backup);
    this.config.storageProvider.set(this.storageKey, events);
  };

  return Destination;
}();

export { Destination };